import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MonthlyFeeCard = ({ feeData, onEdit, onViewDetails, userRole }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'overdue':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'draft':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    })?.format(amount);
  };

  const formatTonnage = (tonnage) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })?.format(tonnage);
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 hover:shadow-md transition-shadow duration-200">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-slate-900">
                {feeData?.month}
              </h3>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                  feeData?.status
                )}`}
              >
                {feeData?.status?.charAt(0)?.toUpperCase() +
                  feeData?.status?.slice(1)}
              </span>
            </div>
            <p className="text-sm text-slate-600">
              Invoice: {feeData?.invoiceNumber}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {userRole === 'Finance' || userRole === 'Admin' ? (
              <Button
                variant="ghost"
                size="sm"
                iconName="Edit2"
                onClick={() => onEdit(feeData)}
              >
                Edit
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <Icon
                name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                size={20}
              />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-xs text-slate-600 mb-1">Total Tonnage</p>
            <p className="text-2xl font-bold text-slate-900">
              {formatTonnage(feeData?.totalTonnage)}
              <span className="text-sm font-normal text-slate-600 ml-1">
                MT
              </span>
            </p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-xs text-slate-600 mb-1">Fee Rate</p>
            <p className="text-2xl font-bold text-slate-900">
              {formatCurrency(feeData?.feeRate)}
              <span className="text-sm font-normal text-slate-600 ml-1">
                /MT
              </span>
            </p>
          </div>
        </div>

        <div className="space-y-3 mb-4">
          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-sm text-slate-600">Gross Fee</span>
            <span className="text-sm font-semibold text-slate-900">
              {formatCurrency(feeData?.grossFee)}
            </span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-sm text-slate-600">
              Advance Payment (33%)
            </span>
            <span className="text-sm font-semibold text-green-600">
              -{formatCurrency(feeData?.advancePayment)}
            </span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-sm text-slate-600">Reimbursable Costs</span>
            <span className="text-sm font-semibold text-red-600">
              -{formatCurrency(feeData?.reimbursableCosts)}
            </span>
          </div>
          <div className="flex items-center justify-between py-3 bg-blue-50 rounded-lg px-3">
            <span className="text-sm font-semibold text-slate-900">
              Net Balance
            </span>
            <span className="text-lg font-bold text-blue-600">
              {formatCurrency(feeData?.netBalance)}
            </span>
          </div>
        </div>

        {isExpanded && (
          <div className="pt-4 border-t border-slate-200 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-600 mb-1">Shipments Count</p>
                <p className="text-sm font-semibold text-slate-900">
                  {feeData?.shipmentsCount}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-600 mb-1">Payment Date</p>
                <p className="text-sm font-semibold text-slate-900">
                  {feeData?.paymentDate || 'Not paid'}
                </p>
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-600 mb-1">Notes</p>
              <p className="text-sm text-slate-700">{feeData?.notes}</p>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                iconName="FileText"
                onClick={() => onViewDetails(feeData)}
                fullWidth
              >
                View Details
              </Button>
              {(userRole === 'Finance' || userRole === 'Admin') &&
                feeData?.status === 'draft' && (
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Send"
                    fullWidth
                  >
                    Generate Invoice
                  </Button>
                )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyFeeCard;